import React, { Component, createRef } from 'react';
import { Grid } from '@material-ui/core';
import {
    SofaCheckbox,
    SofaFormControl,
    AbstractSofaForm,
    SofaFormValidator,
    InputType,
    SofaFormWithGtm,
    SofaFormControlDescriptionWithGtm,
    checkboxValueToBooleanFilter,
    getPropsForSofaInput
} from '@sofology/sofologycomponents';

import './BillingAddressForm.scss';

import { AddressModel, IfcAddressDescription, PostcodeModel, createEmptyAddress } from 'models';
import { BasketErrorModel, BasketHybrisFormErrorModel } from 'models/Errors';
import { AddressEditor } from 'components';
import {
    POSTCODE_IS_EIRE_FOR_IFC_ERROR_MESSAGE,
    BILLING_ADDRESS_SAME_AS_DELIVERY,
    POSTCODE_INVALID_ERROR_MESSAGE
} from '../deliveryDetailsAdditionalData';
import { analyticsHelper, errorToBasketErrorModel, generateRandomId, paymentHelper } from 'helpers';
import { PaymentTypes } from 'models/Payment';
import { SofaFormComponent } from 'interfaces';
import { AddressEditorModes, AddressEditorType } from 'components/AddressEditor/AddressEditor';

const START_BILLING_ADDRESS_FIELD_INDEX = 15;
const GTM_FORM_NAME = 'deliveryDetailsBilling';

interface OwnProps {
    defaultAddress: AddressModel | null;
    ref?: React.RefObject<BillingAddressFormType>;
    deliveryDetailsUpdatingErrors: BasketErrorModel[] | null;
    paymentType: PaymentTypes;
    deliveryAddress: AddressModel | null;
    ifcAddressQuestionSet: IfcAddressDescription;
}

interface OwnState {
    isDefaultAddressStartedEditing: boolean;
    billingAddressTheSameForm: SofaFormWithGtm<BillingAddressTheSameControls>;
    addressFormId: string;
    defaultAddress: AddressModel | null;
}

interface BillingAddressTheSameControls extends AbstractSofaForm {
    isBillingAddressSameAsDelivery: SofaFormControl<boolean>;
}

class BillingAddressForm extends Component<OwnProps, OwnState> implements SofaFormComponent<AddressModel> {
    private addressEditor = React.createRef<AddressEditorType>();

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            isDefaultAddressStartedEditing: false,
            billingAddressTheSameForm: new SofaFormWithGtm<BillingAddressTheSameControls>(
                {
                    isBillingAddressSameAsDelivery: {
                        name: 'isBillingAddressSameAsDelivery',
                        defaultValue: false,
                        filters: [checkboxValueToBooleanFilter()],
                        ref: createRef<SofaCheckbox>(),
                        inputType: InputType.Checkbox,
                        gtmFieldName: 'billingCheck'
                    } as SofaFormControlDescriptionWithGtm<boolean>,
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'billingAddressTheSameForm',
                () => this.state,
                GTM_FORM_NAME,
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel,
                START_BILLING_ADDRESS_FIELD_INDEX
            ),
            addressFormId: generateRandomId(),
            defaultAddress: this.props.defaultAddress
        };
    }

    handleOnAddressChange = (addressComponentMode: AddressEditorModes, address: AddressModel) => {
        if (this.isDefaultAddressUsed() && addressComponentMode !== AddressEditorModes.View && addressComponentMode !== AddressEditorModes.NotInitialized) {
            this.setState({ isDefaultAddressStartedEditing: true });
        }
    };

    render(): JSX.Element {
        const { billingAddressTheSameForm, isDefaultAddressStartedEditing } = this.state;

        const isBillingAddressTheSameAsDelivery = this.isBillingAddressTheSameAsDelivery();

        const deliveryAddress = this.props.deliveryAddress ?? createEmptyAddress();
        const defaultAddress = (this.state.defaultAddress && !isDefaultAddressStartedEditing) ? this.state.defaultAddress : null;

        return (
            <div className='billing-address-form'>
                <div className='billing-address-form__editor'>
                    {!this.isDefaultAddressUsed() && <Grid container spacing={3}>
                        <Grid item xs={12} className='billing-address-form__checkbox-wrapper'>
                            <SofaCheckbox
                                label={BILLING_ADDRESS_SAME_AS_DELIVERY}
                                {...getPropsForSofaInput(billingAddressTheSameForm.controls.isBillingAddressSameAsDelivery)}
                            />
                        </Grid>
                    </Grid>}
                    {isBillingAddressTheSameAsDelivery
                        && this.renderAddress(deliveryAddress, true)
                    }
                    {!isBillingAddressTheSameAsDelivery
                        && <>
                            {this.renderAddress(defaultAddress)}
                        </>
                    }
                </div>
            </div>
        );
    }

    renderAddress(address: AddressModel | null, isReadOnly: boolean = false): JSX.Element {
        return (
            <AddressEditor
                startControlIndex={START_BILLING_ADDRESS_FIELD_INDEX + 1}
                defaultAddress={address}
                readonlyAddress={isReadOnly ? address! : undefined}
                ifcAddressQuestionSet={this.props.ifcAddressQuestionSet}
                gtmFormName={GTM_FORM_NAME}
                addressFormId={this.state.addressFormId}
                ref={this.addressEditor}
                additionalValidators={{
                    postcode: [
                        this.postcodeEirValidator(POSTCODE_IS_EIRE_FOR_IFC_ERROR_MESSAGE),
                        this.postcodeServerValidator(POSTCODE_INVALID_ERROR_MESSAGE)
                    ]
                }}
                onChange={this.handleOnAddressChange}
            />
        );
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        if (this.isBillingAddressTheSameAsDelivery()) {
            return Promise.resolve([]);
        }

        return this.addressEditor.current!.validate();
    };

    public getFormData = (): AddressModel => {
        if (this.isBillingAddressTheSameAsDelivery()) {
            return this.props.deliveryAddress!;
        }

        const billingAddress = this.addressEditor.current!.getFormData();

        return billingAddress;
    };

    postcodeServerValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string, isChangedAfterExternalValidation: boolean) => {
            let isInvalidPostcode = Boolean(
                !isChangedAfterExternalValidation
                && this.props.deliveryDetailsUpdatingErrors
                && this.props.deliveryDetailsUpdatingErrors.find(e =>
                    e instanceof BasketHybrisFormErrorModel
                    && e.subject === 'postalCode'
                    && e.type === 'ValidationError'
                    && e.reason === 'invalid'
                    && e.formName === 'billingDetails'));

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };

    postcodeEirValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            let isInvalidPostcode = Boolean(
                paymentHelper.isCreditRelatedPaymentType(this.props.paymentType)
                && value.length > 0
                && (new PostcodeModel(value)).isEirCode);

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };

    isDefaultAddressUsed(): boolean {
        return Boolean(this.props.defaultAddress && !this.state.isDefaultAddressStartedEditing);
    }

    isBillingAddressTheSameAsDelivery(): boolean {
        return this.state.billingAddressTheSameForm.controls.isBillingAddressSameAsDelivery.value;
    }
}

export default BillingAddressForm;
export type BillingAddressFormType = BillingAddressForm;